import classnames from 'classnames';
import React, { ReactElement, useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Avatar from 'v2/Avatar/Avatar';
import ToggleButton from 'v2/common/ToggleButton/ToggleButton';
import {
  ProposalEntity,
  User,
  TalkjsConversationEntity,
  SearchEntity,
  ReadOnlyUser,
} from 'v2/models';
import restFactory from 'v2/utils/restFactory';
import { useQuery } from 'react-query';
import avatarPerson from '../images/avatarPerson.png';
import avatarPersonNoProfilePic from '../images/avatarPersonNoProfilePic.png';
import ChatMenuItem from './ChatMenuItem';
import styles from './customHeader.module.scss';
import { VerticalDotsIcon } from 'v2/icons';

export interface ChatCustomHeaderProps {
  isDataProvider: boolean;
  proposal_link?: string;
  subject: string;
  proposal?: ProposalEntity;
  fetchAllProposals?: (isBuyer: boolean) => void;
  sendMessage?: (message: string) => void;
  user: User;
  conversation?: TalkjsConversationEntity;
  showDotsMenu?: boolean;
  hideLeftHeader?: boolean;
  onConversationClosed?: (id: string) => void;
  search?: SearchEntity;
  headerLoaded?: () => void;
  isVisible?: boolean;
}

type ProposalResponseKey = 'data_buyer_response' | 'data_provider_response';

const ChatCustomHeader = ({
  isDataProvider,
  proposal_link,
  subject,
  proposal,
  fetchAllProposals,
  sendMessage,
  user,
  conversation,
  showDotsMenu = true,
  hideLeftHeader = false,
  onConversationClosed,
  search,
  headerLoaded,
  isVisible,
}: ChatCustomHeaderProps) => {
  const proposalCurrentUserKey = !isDataProvider
    ? 'data_buyer'
    : 'data_provider';
  const proposalChattingWithUserKey = isDataProvider
    ? 'data_buyer'
    : 'data_provider';

  const [hideIdentity, setHideIdentity] = useState<boolean>(
    !!proposal?.[`${proposalCurrentUserKey}_response` as ProposalResponseKey]
      ?.hide_identity
  );
  const [showMenu, setShowMenu] = useState(false);
  const isReviewerContext =
    conversation?.custom?.chat_context === 'review' &&
    search?.submitted_by?.username === user.username;

  const { data: reviewerHeaders, isSuccess: isReviewerHeadersSuccess } =
    useQuery(
      ['Get reviewer headers', conversation?.custom?.reviewer_id],
      async () => {
        const response = await restFactory.get<ReadOnlyUser>(
          `/dj/api/v1/strapi_user/${conversation?.custom?.reviewer_id}/`,
          null
        );

        return response;
      },
      {
        enabled: isReviewerContext,
      }
    );

  const getReviewSubmitter = () => {
    let headerChatElements = null;
    const submittedBy = search?.submitted_by;
    const submittedByOrganizationName = search?.organization?.name;
    if (submittedBy?.username !== user.username) {
      headerChatElements = {
        first_name: submittedBy?.first_name || '',
        last_name: submittedBy?.last_name || '',
        organization_name: submittedByOrganizationName || '',
        avatar_url: submittedBy?.avatar_url,
        full_name: submittedBy?.first_name + ' ' + submittedBy?.last_name,
      };
    } else {
      headerChatElements = {
        first_name: reviewerHeaders?.first_name || '',
        last_name: reviewerHeaders?.last_name || '',
        organization_name: reviewerHeaders?.organization_name || '',
        avatar_url: reviewerHeaders?.avatar_url,
        full_name:
          reviewerHeaders?.first_name + ' ' + reviewerHeaders?.last_name,
      };
    }
    return headerChatElements;
  };

  const getSubmittedBy = () => {
    let headerChatElements = null;
    if (conversation?.custom?.chat_context === 'review') {
      headerChatElements = getReviewSubmitter();
    } else if (proposal) {
      const submittedBy =
        proposal[
          `${proposalChattingWithUserKey}_response` as ProposalResponseKey
        ]?.submitted_by;
      if (submittedBy) {
        const avatar_url = submittedBy?.avatar?.url;
        headerChatElements = {
          ...submittedBy,
          avatar_url,
          full_name: submittedBy?.first_name + ' ' + submittedBy?.last_name,
        };
      }
    }
    headerLoaded?.();
    return headerChatElements;
  };

  if (isReviewerContext && !isReviewerHeadersSuccess) {
    return null;
  }

  const submittedBy = getSubmittedBy();
  const isAnonymous = !submittedBy;
  const showHideIdentityToggle =
    !isDataProvider || user.data_provider.hide_identity_allowed;

  const Subtitle = ({ children }: { children: ReactElement }) =>
    isDataProvider && proposal_link ? (
      <Link to={proposal_link} className={styles.linkContainer}>
        <span className={classnames('proposal-link', styles.proposalLink)}>
          {children}
        </span>
      </Link>
    ) : (
      children
    );

  const shareContactInfo = () => {
    sendMessage?.(`Contact me at: ${user.email}`);
  };

  const renderOverlay = (proposal: ProposalEntity) => {
    return (
      <Popover id="chatMenu" className={styles.popover}>
        <ul
          data-testid="menuListContainer"
          className={classnames(styles.menuList)}
        >
          {showHideIdentityToggle ? (
            <ChatMenuItem
              text="Hide Identity"
              action={
                <ToggleButton
                  className={styles.toggleBtn}
                  checked={hideIdentity}
                  onChange={async (e) => {
                    setHideIdentity(e.currentTarget.checked);
                    await updateHideIdentity(
                      proposal.id,
                      isDataProvider,
                      e.currentTarget.checked
                    );
                    fetchAllProposals?.(!isDataProvider);
                    setShowMenu(false);
                  }}
                  ariaLabel="toggleHideIdentity"
                />
              }
            />
          ) : null}
          <ChatMenuItem
            className="selectable cursor-pointer"
            title="Share contact information"
            text="Insert email into chat"
            onClick={() => {
              shareContactInfo();
              setShowMenu(false);
            }}
          />
          {conversation?.id ? (
            <ChatMenuItem
              className={classnames('selectable cursor-pointer text-danger')}
              text="End conversation"
              onClick={() => {
                endConversation(conversation?.id).then(
                  () => onConversationClosed?.(conversation?.id)
                );
                setShowMenu(false);
              }}
            />
          ) : null}
        </ul>
      </Popover>
    );
  };

  const getUserDetails = () => {
    if (conversation?.custom?.chat_context === 'review') {
      return (
        <>
          <span className={classnames(styles.title)}>
            {`${submittedBy?.full_name} -
        ${submittedBy?.organization_name}`}
          </span>
          <p className={classnames(styles.headerSubject)}>
            {submittedBy?.first_name}
          </p>
        </>
      );
    } else {
      return (
        <>
          <span className={classnames(styles.title)}>
            {!isAnonymous ? `${submittedBy?.full_name} - ` : ''}
            {proposal &&
              getDetail(isAnonymous, proposal?.[proposalChattingWithUserKey])}
          </span>
          <Subtitle>
            <p className={classnames(styles.headerSubject)}>{subject}</p>
          </Subtitle>
        </>
      );
    }
  };

  const renderCustomHeader = () => {
    return (
      <div
        className={classnames(styles.headerRow, {
          [styles.isVisible]: isVisible,
        })}
      >
        {hideLeftHeader ? null : <div className={styles.left}></div>}
        <div className={classnames(styles.right, styles.chattingWithInfo)}>
          <div className={styles.userInfo}>
            <Avatar
              initials={submittedBy?.full_name?.[0]}
              url={
                isAnonymous
                  ? avatarPerson
                  : submittedBy?.avatar_url
                  ? submittedBy?.avatar_url
                  : avatarPersonNoProfilePic
              }
            />
            <div className={classnames(styles.userDetails)}>
              {getUserDetails()}
            </div>
          </div>
          <div>
            {!isDataProvider && hideIdentity && (
              <span className={styles.identityHidden}>Identity Hidden</span>
            )}
            {showDotsMenu && proposal ? (
              <OverlayTrigger
                trigger="click"
                placement="bottom-end"
                rootClose={true}
                show={showMenu}
                onToggle={(show) => {
                  setShowMenu(show);
                }}
                overlay={renderOverlay(proposal)}
              >
                <button
                  data-testid="chatMenuButton"
                  className={classnames(styles.menuButton)}
                >
                  <VerticalDotsIcon />
                </button>
              </OverlayTrigger>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  return conversation?.custom?.chat_context === 'review' || proposal
    ? renderCustomHeader()
    : null;
};

export default ChatCustomHeader;

async function updateHideIdentity(
  proposalId: number,
  isDataProvider: boolean,
  hideIdentity: boolean
) {
  const key = isDataProvider ? 'data_provider_response' : 'data_buyer_response';
  await restFactory.put(`/proposals/${proposalId}/${key.replace(/_/g, '-')}`, {
    [key]: { hide_identity: hideIdentity },
  });
}

async function endConversation(id: string) {
  await restFactory.put(`/conversations/${id}/close`, {});
}

function getDetail(
  isAnonymous: boolean,
  data: ProposalEntity['data_buyer'] | ProposalEntity['data_provider']
) {
  if (isAnonymous) {
    return data.classification;
  }
  return data?.name;
}
